import {
  AiparserCreations,
  AnalyticsDomainResponseItem,
  AnalyticsGraphDataPoint,
  ChartsData,
  DomainTableRow,
  FilterOptions,
  FilterOptionsResponse,
  OverviewStats,
  OverviewStatsSummary,
} from "./types";
import { toNumber, fmtNumber } from "utils";

// Map graph data from API
export const mapGraphData = (
  data: AnalyticsGraphDataPoint[] = [],
): ChartsData[] => {
  return data.map((row: AnalyticsGraphDataPoint) => {
    const success = toNumber(row.successful_requests);
    const failedRequests = toNumber(row.failed_requests);
    const cancelledRequests = toNumber(row.cancelled_requests);
    const failure = failedRequests + cancelledRequests;
    const concurrentThreads = toNumber(row.avg_concurrency);

    return {
      startOfInterval: row.day,
      success,
      // Include cancelled requests in failure count for number consistency
      failure,
      concurrentThreads,
    };
  });
};

// Process filter options from API
export const mapFilterOptions = (
  filterData?: FilterOptionsResponse | null,
): FilterOptions => {
  if (!filterData) {
    return {
      domainCount: 0,
      domains: [],
      countries: [],
      productTypes: [],
      parameters: [],
    };
  }

  // provide default values if one is not present
  const {
    totalDomainCount = 0,
    domains = [],
    countries = [],
    productTypes = [],
    parameters = [],
  } = filterData;

  return {
    domainCount: toNumber(totalDomainCount),
    domains,
    countries,
    productTypes,
    parameters,
  };
};

// Maps overview stats from backend API format to UI format
// Backend now calculates success_rate and credits_per_request
export const mapOverviewStats = (
  stats?: OverviewStats | null,
  aiparserCreations?: AiparserCreations | null,
): OverviewStatsSummary | null => {
  if (!stats) return null;

  return {
    requestsSent: toNumber(stats.total_requests),
    successRate: toNumber(stats.success_rate),
    avgResponseTime: toNumber(stats.avg_response_time_ms),
    avgConcurrency: toNumber(stats.avg_concurrency),
    domainsScraped: toNumber(stats.domains_scraped),
    creditsPerRequest: toNumber(stats.credits_per_request),
    paygRequests: toNumber(stats.total_payg_requests),
    paygCredits: toNumber(stats.total_payg_credits),
    cancelledRequests: toNumber(stats.cancelled_requests),
    cancelledCredits: toNumber(stats.cancelled_credits),
    parsersCreated: toNumber(aiparserCreations?.totals?.parsers_created),
    parserCreditsConsumed: toNumber(aiparserCreations?.totals?.credit_cost),
  };
};

// Maps domain list from API to formatted table rows for UI display
export const mapDomainListToTableData = (
  data: AnalyticsDomainResponseItem[] = [],
): DomainTableRow[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((entry, index) => {
    return {
      domain: entry.domain || `Unknown-${index}`,
      requests: fmtNumber(toNumber(entry.requests)),
      cancelledRequests: fmtNumber(toNumber(entry.cancelled_requests)),
      successRate: `${toNumber(entry.success_rate).toFixed(1)}%`,
      credits: fmtNumber(toNumber(entry.credit_costs)),
      ultraPremium: fmtNumber(toNumber(entry.ultra_premium_count)),
      premium: fmtNumber(toNumber(entry.premium_count)),
      render: fmtNumber(toNumber(entry.render_count)),
      aiParser: fmtNumber(toNumber(entry.aiparser_credits)),
      location: entry.location ?? undefined,
    };
  });
};
